import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import { useAppDispatch } from "../../hooks/useDispatch";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import service from "../../services/menu";
import roleService from "../../services/role";
import { actions } from "../../store/reducers/global";
import queryDefault from "../../utils/queryDefault";

interface Menu {
  id: number;
  name: string;
  order: string;
  is_create: boolean;
  is_read: boolean;
  is_update: boolean;
  is_delete: boolean;
  link: string | null;
}

const RoleEdit: React.FC = () => {
  const { id } = useParams();
  const roleId = Number(id);
  const [menus, setMenus] = useState<Menu[]>([]);
  const { user } = useAppSelector((state) => state.global);

  const dispatch = useAppDispatch()
  const notification = useNotification();
  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      const { data } = await service.getMenuByRoleId(roleId);
      setMenus(data);
    };
    fetchData();
  }, [roleId]);

  const handleCheckboxChange = (menuId: number, permission: keyof Menu) => {
    setMenus((prevMenus) => {
      return prevMenus.map((menu) => {
        if (menu.id === menuId) {
          if (permission !== "is_read") {
            // If "Create", "Delete", or "Update" is checked, auto-check "Read"
            if (!menu[permission]) {
              return { ...menu, [permission]: true, is_read: true };
            }
            // If "Create", "Delete", or "Update" is unchecked, leave "Read" as-is
            else {
              return { ...menu, [permission]: false };
            }
          } else if (permission === "is_read") {
            return { ...menu, is_read: !menu.is_read };
          } else {
            return { ...menu, [permission]: !menu[permission] };
          }
        }
        return menu;
      });
    });

    // Cascade parent menu read updates only if "Create", "Delete", "Update", or "Read" is changed
    updateParentMenuReadState(menuId);
  };

  // Recursive function to update parent menu's `is_read` based on submenu's state
  const updateParentMenuReadState = (submenuId: number) => {
    setMenus((prevMenus) => {
      const submenu = prevMenus.find((menu) => menu.id === submenuId);
      if (!submenu) return prevMenus;
      if (!submenu.is_read) return prevMenus;
      // Get parent order by truncating the last two digits
      const parentOrder = submenu.order.slice(0, -2);

      return prevMenus.map((menu) => {
        // Check if the parent order exists in the menu list and ensure it's marked as read
        if (menu.order === parentOrder && !menu.is_read) {
          return { ...menu, is_read: true };
        }
        return menu;
      });
    });

    // Recursively ensure all parents have `Read` permission
    const submenu = menus.find((menu) => menu.id === submenuId);
    if (submenu) {
      const parentOrder = submenu.order.slice(0, -2);
      const parentMenu = menus.find((menu) => menu.order === parentOrder);
      if (parentMenu) {
        updateParentMenuReadState(parentMenu.id);
      }
    }
  };

  // Calculate indentation based on order string length
  const getIndentationLevel = (order: string) => {
    const level = Math.floor((order.length - 1) / 2); // e.g., 01 -> 0, 0101 -> 1, 010101 -> 2
    return `${level * 20}px`;
  };

  const updateRole = useMutation("update_role", roleService.updateRole, {
    onSuccess: () => {
      navigate("/app/users/roles");
      dispatch(actions.reloadMenu())
      notification.onOpen({
        message: "Role was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: (error: any) => {
      console.log(error);
      notification.onOpen({
        message: error?.response?.data?.error || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  // Handle form submission
  const handleSubmit = () => {
    const selectedMenus = menus.filter(
      (menu) =>
        menu.is_create || menu.is_read || menu.is_update || menu.is_delete
    );
    const payload = {
      username: user.username,
      menus: selectedMenus,
      id: roleId,
    };

    updateRole.mutate(payload);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Update Role"
        breadcrumbs={[
          { label: "Roles", href: "/app/users/roles" },
          { label: "Edit Role", href: `/app/users/roles/edit/${id}` },
        ]}
      />

      <ProtectedPermissionPage requiredPermissions={{is_update: true}} menuSlug="roles">
        <Box sx={{ p: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Menu</TableCell>
                  <TableCell align="center">Read</TableCell>
                  <TableCell align="center">Create</TableCell>
                  <TableCell align="center">Update</TableCell>
                  <TableCell align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menus.map((menu) => (
                  <TableRow key={menu.id}>
                    <TableCell>
                      <Box sx={{ pl: getIndentationLevel(menu.order) }}>
                        {menu.name}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={menu.is_read}
                        onChange={() =>
                          handleCheckboxChange(menu.id, "is_read")
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        disabled={menu.link === null}
                        checked={menu.is_create}
                        onChange={() =>
                          handleCheckboxChange(menu.id, "is_create")
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        disabled={menu.link === null}
                        checked={menu.is_update}
                        onChange={() =>
                          handleCheckboxChange(menu.id, "is_update")
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        disabled={menu.link === null}
                        checked={menu.is_delete}
                        onChange={() =>
                          handleCheckboxChange(menu.id, "is_delete")
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ mt: 2 }}
          >
            Save Role
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ mt: 2, ml: 2 }}
          >
            Cancel
          </Button>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
};

export default RoleEdit;
